import React, { useMemo, useState } from 'react';
import { CheckCircle2, Shield, Users, Building2, Info, AlertTriangle, Eye, Edit } from 'lucide-react';

// ── Static mockup — demonstrates the NEW department-driven feature-mapping flow ──
// Two variants: (A) Team Management (real users) and (B) Sub-User creation.
// This is a non-functional preview for design approval. No API calls.

// Sample department → allowed functionalities (mirrors /api/my-department-features).
const DEPT_FEATURES = {
  'Sales': [
    { group: 'Common', items: [['org-tree', 'Org Tree'], ['mobile-lookup', 'Mobile Lookup']] },
    { group: 'Field Activity', items: [['field-activity', 'Overview'], ['travel-plans', 'Travel Plans'], ['visits', 'Visits']] },
    { group: 'FASTag', items: [['revenue', 'Customer Onboarding'], ['performance', 'Performance']] },
    { group: 'Operations', items: [['leads', 'Leads'], ['lead-distribution', 'Lead Distribution'], ['task-tracker', 'Task Tracker'], ['sales-points', 'Sales Points']] },
  ],
  'Operations': [
    { group: 'Field Activity', items: [['field-activity', 'Overview'], ['visits', 'Visits']] },
    { group: 'Operations', items: [['approvals', 'Approvals'], ['attendance', 'Attendance'], ['expense-claims', 'Expense Claims'], ['leads', 'Leads']] },
  ],
  'Accounts / Finance': [
    { group: 'FASTag', items: [['idfc-toll', 'IDFC Toll Txns'], ['payout', 'FASTag Payout']] },
    { group: 'Hitch Zone', items: [['banking', 'Hitch Zone Txns']] },
    { group: 'Operations', items: [['expense-claims', 'Expense Claims'], ['txn-audit', 'Txn Auditor']] },
  ],
  'Digital Marketing': [
    { group: 'Operations', items: [['sheet-import', 'Google Sheet Import'], ['leads', 'Leads']] },
  ],
  'Support (empty)': [], // demonstrates the "no functionalities" notice
};
const DEPTS = Object.keys(DEPT_FEATURES);

function FeatureMappingBlock({ dept, subUserMode }) {
  const groups = DEPT_FEATURES[dept] || [];
  const allKeys = useMemo(() => groups.flatMap(g => g.items.map(([k]) => k)), [groups]);
  // Default: everything the department allows is ticked (mode 'edit' for sub-user).
  const [sel, setSel] = useState(() => {
    const m = {}; allKeys.forEach(k => { m[k] = subUserMode ? 'edit' : true; }); return m;
  });
  // reset when dept changes
  React.useEffect(() => {
    const m = {}; allKeys.forEach(k => { m[k] = subUserMode ? 'edit' : true; }); setSel(m);
  }, [dept]); // eslint-disable-line

  const count = Object.keys(sel).length;

  if (!dept) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-400">
        Choose a department above to see which functionalities can be mapped.
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-2.5">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-800">
          <p className="font-semibold">This department has no functionalities yet.</p>
          <p className="text-xs text-amber-700 mt-0.5">
            Configure it in <b>Admin → Department Access</b> first, then come back to map functionalities to this user.
          </p>
        </div>
      </div>
    );
  }

  const toggle = (k) => setSel(s => {
    const n = { ...s };
    if (n[k]) delete n[k]; else n[k] = subUserMode ? 'edit' : true;
    return n;
  });
  const setMode = (k, mode) => setSel(s => ({ ...s, [k]: mode }));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-slate-500">
          Showing only <b className="text-slate-700">{dept}</b>'s functionalities. Tick the ones this user should get —
          they will see <b>only</b> the mapped items.
        </p>
        <span className="text-[10px] font-semibold bg-amber-100 text-amber-700 rounded px-2 py-0.5">{count} mapped</span>
      </div>
      <div className="space-y-4 max-h-80 overflow-y-auto pr-1">
        {groups.map(g => (
          <div key={g.group}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">{g.group}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {g.items.map(([k, label]) => {
                const granted = !!sel[k];
                return (
                  <div key={k} className={`flex items-center justify-between rounded-lg border px-3 py-2 ${granted ? 'bg-white border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                    <label className="flex items-center gap-2 cursor-pointer flex-1" onClick={() => toggle(k)}>
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${granted ? 'bg-amber-500 border-amber-500' : 'bg-white border-slate-300'}`}>
                        {granted && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-xs font-medium text-slate-700">{label}</span>
                    </label>
                    {subUserMode && granted && (
                      <div className="flex gap-1 ml-2">
                        <button onClick={() => setMode(k, 'view')} className={`text-[10px] px-2 py-0.5 rounded border font-medium flex items-center gap-1 ${sel[k] === 'view' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-slate-400 border-slate-200'}`}><Eye className="w-3 h-3" />View</button>
                        <button onClick={() => setMode(k, 'edit')} className={`text-[10px] px-2 py-0.5 rounded border font-medium flex items-center gap-1 ${sel[k] === 'edit' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-400 border-slate-200'}`}><Edit className="w-3 h-3" />Edit</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DeptPicker({ dept, setDept }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600">Department *</label>
      <select
        value={dept}
        onChange={e => setDept(e.target.value)}
        className="mt-1 w-full h-9 text-sm border border-slate-300 rounded-md px-2 bg-white"
      >
        <option value="">— Select department —</option>
        {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
      </select>
    </div>
  );
}

export default function DeptFeatureMappingPreview() {
  const [deptA, setDeptA] = useState('Sales');
  const [deptB, setDeptB] = useState('Operations');

  return (
    <div className="min-h-screen bg-slate-100 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">New Flow — Department-Driven Feature Mapping</h1>
          <p className="text-sm text-slate-500 mt-1">
            Static mockup for approval. The big "all features" matrix is removed. After choosing a department, the form
            shows <b>only that department's allowed functionalities</b> (from Admin → Department Access) and you map a subset.
            The user then sees <b>only the mapped functionalities</b>.
          </p>
        </div>

        {/* Flow strip */}
        <div className="flex flex-wrap items-center gap-2 text-xs font-medium">
          {['1. Enter user details', '2. Choose Department', '3. Department functionalities load', '4. Map a subset', '5. Save — user sees only mapped items'].map((s, i) => (
            <React.Fragment key={s}>
              <span className="bg-white border border-slate-200 rounded-full px-3 py-1.5 text-slate-600 shadow-sm">{s}</span>
              {i < 4 && <span className="text-slate-300">→</span>}
            </React.Fragment>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* A — Team Management (real user) */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
              <Users className="w-5 h-5 text-amber-500" />
              <div>
                <h2 className="text-base font-bold text-slate-800">A · Team Management — Add User</h2>
                <p className="text-[11px] text-slate-400">Real user. Binary access (no view/edit modes).</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-600">Full Name</label><input className="mt-1 w-full h-9 text-sm border border-slate-300 rounded-md px-2" placeholder="Jane Doe" /></div>
              <div><label className="text-xs font-medium text-slate-600">Email</label><input className="mt-1 w-full h-9 text-sm border border-slate-300 rounded-md px-2" placeholder="jane@company.com" /></div>
              <div><label className="text-xs font-medium text-slate-600">Role</label><input className="mt-1 w-full h-9 text-sm border border-slate-300 rounded-md px-2" placeholder="Team Leader" /></div>
              <DeptPicker dept={deptA} setDept={setDeptA} />
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
              <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Functionalities to map</p>
              <FeatureMappingBlock dept={deptA} subUserMode={false} />
            </div>
            <div className="flex items-start gap-1.5 text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-2 py-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>If you leave every functionality ticked, the user simply inherits the whole department (default). Untick some to give a narrower, per-user subset.</span>
            </div>
          </div>

          {/* B — Sub-User */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
              <Shield className="w-5 h-5 text-amber-500" />
              <div>
                <h2 className="text-base font-bold text-slate-800">B · Sub-User — Create</h2>
                <p className="text-[11px] text-slate-400">Limited login. Per-functionality View / Edit mode.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-600">Full Name</label><input className="mt-1 w-full h-9 text-sm border border-slate-300 rounded-md px-2" placeholder="Audit Viewer" /></div>
              <div><label className="text-xs font-medium text-slate-600">Email</label><input className="mt-1 w-full h-9 text-sm border border-slate-300 rounded-md px-2" placeholder="viewer@company.com" /></div>
              <div><label className="text-xs font-medium text-slate-600">Password</label><input className="mt-1 w-full h-9 text-sm border border-slate-300 rounded-md px-2" placeholder="Min 6 chars" /></div>
              <DeptPicker dept={deptB} setDept={setDeptB} />
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-3.5 h-3.5 text-slate-400" />
                <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">Feature Access (scoped to department)</p>
              </div>
              <FeatureMappingBlock dept={deptB} subUserMode={true} />
            </div>
          </div>
        </div>

        <div className="text-center text-xs text-slate-400 pt-2">
          Try switching either <b>Department</b> dropdown — including <b>“Support (empty)”</b> to see the no-functionalities notice.
        </div>
      </div>
    </div>
  );
}
